"use client"

import { useState, useEffect } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "./contexts/AuthContext"

const navItems = [
  {
    title: "Explore collection",
    items: [
      { title: "CUT emerald", href: "/shop-cutemerald" },
      { title: "Rough Emerald", href: "/shop-roughemerald" },
    ],
  },
]

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [activeSubDropdown, setActiveSubDropdown] = useState(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const toggleMobileMenu = () => setIsMenuOpen((prev) => !prev)

  const toggleDropdown = (menu) => {
    if (activeDropdown === menu) {
      setActiveDropdown(null)
      setActiveSubDropdown(null)
    } else {
      setActiveDropdown(menu)
      setActiveSubDropdown(null)
    }
  }

  const toggleSubDropdown = (submenu, event) => {
    event.stopPropagation()
    setActiveSubDropdown(activeSubDropdown === submenu ? null : submenu)
  }

  const closeDropdowns = () => {
    setActiveDropdown(null)
    setActiveSubDropdown(null)
  }

  const handleLogout = async () => {
    await logout()
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0)
    }

    const handleClickOutside = () => {
      closeDropdowns()
    }

    window.addEventListener("scroll", handleScroll)
    window.addEventListener("click", handleClickOutside)

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("click", handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      // Only close the menu on larger screens
      if (window.innerWidth >= 1024 && isMenuOpen) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [isMenuOpen])

  // New useEffect to close mobile menu on navigation
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])

  const renderDropdownItems = (items, isNested = false) => (
    <div
      className={`${
        isNested ? "absolute left-full top-0 mt-0" : "absolute left-0 mt-2"
      } w-48 bg-gray-800 text-white shadow-lg rounded-lg z-50`}
    >
      {items.map((item) => (
        <div key={item.title} className="relative">
          {item.items ? (
            <>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-600 flex items-center justify-between font-serif text-sm sm:text-base"
                onClick={(e) => toggleSubDropdown(item.title, e)}
              >
                {item.title}
                <svg
                  className={`w-4 h-4 ml-2 transition-transform duration-300 ${
                    activeSubDropdown === item.title ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {activeSubDropdown === item.title && renderDropdownItems(item.items, true)}
            </>
          ) : (
            <Link to={item.href} className="block px-4 py-2 hover:bg-gray-600 font-serif text-sm sm:text-base">
              {item.title}
            </Link>
          )}
        </div>
      ))}
    </div>
  )

  return (
    <nav
      className={`sticky top-0 w-full transition-all text-white z-50 ${
        isScrolled ? "bg-opacity-50 backdrop-blur-sm" : "bg-[#1a1a1a]"
      }`}
      style={{
        background: isScrolled ? "rgba(26, 26, 26, 0.8)" : "#1a1a1a",
        fontFamily: "'Playfair Display', serif",
      }}
    >
      <div className="container mx-auto flex items-center justify-between py-2 sm:py-3 md:py-4 px-3 sm:px-4 md:px-6 lg:px-8">
        <Link to="/" className="flex items-center">
          <div className="flex flex-row space-x-2 ml-0 sm:ml-2">
            <h1 className="text-white text-xl sm:text-xl md:text-2xl font-serif tracking-wide">Dubai</h1>
            <h1 className="text-emerald-400 text-xl sm:text-xl md:text-2xl font-serif tracking-wide">
              Panjshir EmeraldVault
            </h1>
          </div>
        </Link>

        <button
          className="lg:hidden focus:outline-none p-1"
          onClick={(e) => {
            e.stopPropagation()
            toggleMobileMenu()
          }}
          aria-label="Toggle mobile menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
            />
          </svg>
        </button>

        <div className="hidden lg:flex items-center space-x-2 lg:space-x-4">
          {location.pathname !== "/" && (
            <Link to="/" className="px-2 py-1 text-sm lg:text-base hover:text-emerald-400 font-serif transition-colors">
              Home
            </Link>
          )}
          {navItems.map((item) => (
            <div key={item.title} className="relative">
              <button
                className="px-2 py-1 text-sm lg:text-base hover:text-emerald-400 cursor-pointer flex items-center font-serif transition-colors"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleDropdown(item.title)
                }}
              >
                {item.title}
                <svg
                  className={`w-4 h-4 ml-1 transition-transform duration-300 ${
                    activeDropdown === item.title ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {activeDropdown === item.title && renderDropdownItems(item.items)}
            </div>
          ))}
          <Link
            to="/pricing-plans"
            className="px-2 py-1 text-sm lg:text-base hover:text-emerald-400 font-serif transition-colors"
          >
            Become an Emerald Stake Holder
          </Link>
          <Link
            to="/mining"
            className="px-2 py-1 text-sm lg:text-base hover:text-emerald-400 font-serif transition-colors"
          >
            Mining
          </Link>
          <Link
            to="/whyus"
            className="px-2 py-1 text-sm lg:text-base hover:text-emerald-400 font-serif transition-colors"
          >
            Why Choose Us
          </Link>
          {user ? (
            <>
              <span className="px-2 py-1 text-sm lg:text-base text-gray-300 font-serif">Welcome, {user.firstname}</span>
              <Link
                to={
                  user.role === "Admin" || user.role === "Super Admin"
                    ? "/dashboard/admin"
                    : `/user-dashboard/${user._id}`
                }
                className="px-3 py-1.5 lg:px-4 lg:py-2 text-sm lg:text-base rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors font-serif"
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="px-3 py-1.5 lg:px-4 lg:py-2 text-sm lg:text-base rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors font-serif"
              >
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="px-3 py-1.5 lg:px-4 lg:py-2 text-sm lg:text-base rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors font-serif"
            >
              Log In
            </Link>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-gray-800 px-4 py-2 shadow-lg overflow-y-auto max-h-[calc(100vh-60px)]">
          {location.pathname !== "/" && (
            <Link to="/" className="block py-3 border-b border-gray-700 hover:bg-gray-700 font-serif transition-colors">
              Home
            </Link>
          )}
          {navItems.map((item) => (
            <div key={item.title} className="relative border-b border-gray-700">
              <button
                className="w-full text-left py-3 hover:bg-gray-700 flex items-center justify-between font-serif transition-colors"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleDropdown(item.title)
                }}
              >
                {item.title}
                <svg
                  className={`w-4 h-4 ml-2 transition-transform duration-300 ${
                    activeDropdown === item.title ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {activeDropdown === item.title && (
                <div className="bg-gray-900 rounded-md my-1">
                  {item.items.map((subItem) => (
                    <div key={subItem.title}>
                      {subItem.items ? (
                        <>
                          <button
                            className="w-full text-left px-6 py-3 hover:bg-gray-800 flex items-center justify-between font-serif transition-colors"
                            onClick={(e) => toggleSubDropdown(subItem.title, e)}
                          >
                            {subItem.title}
                            <svg
                              className={`w-4 h-4 ml-2 transition-transform duration-300 ${
                                activeSubDropdown === subItem.title ? "rotate-180" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {activeSubDropdown === subItem.title && (
                            <div className="bg-gray-950 rounded-md my-1 mx-2">
                              {subItem.items.map((nestedItem) => (
                                <Link
                                  key={nestedItem.title}
                                  to={nestedItem.href}
                                  className="block px-8 py-3 hover:bg-gray-800 font-serif transition-colors"
                                >
                                  {nestedItem.title}
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <Link
                          to={subItem.href}
                          className="block px-6 py-3 hover:bg-gray-800 font-serif transition-colors"
                        >
                          {subItem.title}
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link
            to="/pricing-plans"
            className="block py-3 border-b border-gray-700 hover:bg-gray-700 font-serif transition-colors"
          >
            Become an Emerald Stake Holder
          </Link>
          <Link
            to="/mining"
            className="block py-3 border-b border-gray-700 hover:bg-gray-700 font-serif transition-colors"
          >
            Mining
          </Link>
          <Link
            to="/whyus"
            className="block py-3 border-b border-gray-700 hover:bg-gray-700 font-serif transition-colors"
          >
            Why Choose Us
          </Link>

          {user ? (
            <div className="py-4 space-y-3">
              <div className="py-2 text-gray-300 font-serif border-b border-gray-700">Welcome, {user.firstname}</div>
              <Link
                to={
                  user.role === "Admin" || user.role === "Super Admin"
                    ? "/dashboard/admin"
                    : `/user-dashboard/${user._id}`
                }
                className="block w-full text-center py-2.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors font-serif"
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="block w-full text-center py-2.5 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors font-serif"
              >
                Logout
              </button>
            </div>
          ) : (
            <div className="py-4">
              <Link
                to="/login"
                className="block w-full text-center py-2.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors font-serif"
              >
                Log In
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  )
}

export default Navbar
